/**
 * Inventory List Component
 *
 * Displays inventory items with filtering, sorting, and stock management
 *
 * @module pages/inventory/InventoryList
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Form,
  InputGroup,
  Dropdown,
  Spinner,
  Alert,
  Collapse,
  Nav,
} from 'react-bootstrap';
import { DataTable } from '../../components/common/DataTable';
import KebabMenu from '../../components/common/KebabMenu';
import {
  listInventory,
  getInventorySummary,
  getInventoryBatches
} from '../../services/inventoryService';
import PaginationBar from '../../components/common/PaginationBar';
import { unwrapServiceResult } from '../../utils/serviceResult';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { isLowStock } from '../../utils/calculations';
import { useAuth } from '../../contexts/AuthContext';
import { INVENTORY_CATEGORY_LABELS } from '../../utils/constants';
import StockMovementModal from '../../components/inventory/StockMovementModal';
import useViewToggle from '../../hooks/useViewToggle';
import ViewToggle from '../../components/common/ViewToggle';
import DashboardLayout from '../../components/layout/DashboardLayout';
import './InventoryList.css';

const InventoryList = () => {
  const navigate = useNavigate();
  const { isAdmin, isTechnician } = useAuth();
  const { viewMode, setViewMode, isTableView } = useViewToggle();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [showLowStockOnly, setShowLowStockOnly] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [selectedItem, setSelectedItem] = useState(null);
  const [showMovementModal, setShowMovementModal] = useState(false);
  const [batchCounts, setBatchCounts] = useState({});
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('materiales');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // Ref for debounce timer
  const searchTimeoutRef = useRef(null);

  const canEdit = isAdmin || isTechnician;

  // Cualquier cambio en el conjunto (búsqueda, categoría, pestaña, bajo stock,
  // orden) invalida la página actual.
  useEffect(() => {
    setPage(0);
  }, [searchTerm, categoryFilter, showLowStockOnly, activeTab, sortBy]);

  const queryFilters = useMemo(
    () => ({
      search: searchTerm,
      category: categoryFilter,
      // Pestaña, orden y "bajo stock" viajan al servicio: los tres decidían
      // qué filas se muestran y los tres se aplicaban DESPUÉS del recorte de
      // 20 filas, así que la pantalla mostraba un subconjunto arbitrario y lo
      // presentaba como el inventario completo.
      itemType: activeTab === 'herramientas' ? 'tool' : 'material',
      sortBy,
      lowStock: showLowStockOnly,
      page,
      pageSize,
      // #522 — esta pantalla SÍ muestra precio de venta y costo promedio, y es
      // admin-only por `RoleGuard` en App.js. El opt-in es explícito porque el
      // servicio ya no manda esas dos columnas por defecto: viajaban a los tres
      // caminos que alcanza el técnico sin que ninguno las pintara.
      includeCosts: true,
    }),
    [searchTerm, categoryFilter, activeTab, sortBy, showLowStockOnly, page, pageSize]
  );

  // Fetch inventory. `unwrapServiceResult` hace que un fallo llegue como error de
  // react-query en vez de como lista vacía (ver utils/serviceResult.js).
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: ['inventory', queryFilters],
    queryFn: () => unwrapServiceResult(listInventory(queryFilters)),
    placeholderData: (previousData) => previousData, // Keep previous data while fetching new
  });

  // Fetch inventory summary statistics
  const { data: summaryData } = useQuery({
    queryKey: ['inventory-summary'],
    queryFn: getInventorySummary,
  });

  const inventoryItems = useMemo(() => data?.data || [], [data]);
  const totalCount = data?.count || 0;
  const summary = summaryData?.data || {};

  /**
   * Handle search input change with debounce (300ms delay)
   */
  const handleSearchChange = useCallback((value) => {
    setSearchInput(value);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout
    searchTimeoutRef.current = setTimeout(() => {
      setSearchTerm(value);
    }, 300);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Computed: count active filters (excluding search)
  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (categoryFilter) count++;
    if (showLowStockOnly) count++;
    if (sortBy !== 'name') count++;
    return count;
  }, [categoryFilter, showLowStockOnly, sortBy]);

  const hasActiveFilters = activeFiltersCount > 0 || searchTerm;

  /**
   * Handle clear all filters
   */
  const handleClearFilters = () => {
    setCategoryFilter('');
    setShowLowStockOnly(false);
    setSortBy('name');
    setSearchTerm('');
    setSearchInput('');
  };

  /**
   * Toggle filters panel
   */
  const toggleFiltersPanel = () => setIsFiltersExpanded((prev) => !prev);

  // Fetch batch counts for visible items
  React.useEffect(() => {
    const fetchBatchCounts = async () => {
      if (!inventoryItems.length) return;

      const counts = {};
      for (const item of inventoryItems) {
        try {
          const { data: batches } = await getInventoryBatches(item.id, { activeOnly: true });
          counts[item.id] = batches?.length || 0;
        } catch (e) {
          counts[item.id] = 0;
        }
      }
      setBatchCounts(counts);
    };

    fetchBatchCounts();
  }, [inventoryItems]);

  // El orden y la separación Materiales/Herramientas los resuelve `listInventory`
  // (ver queryFilters arriba). Reordenar o refiltrar acá volvería a operar sobre
  // la página ya recortada, que es exactamente el bug que se está arreglando.
  const tabFilteredItems = inventoryItems;

  // DataTable columns
  const inventoryColumns = useMemo(() => [
    {
      id: 'image',
      header: '',
      accessor: () => '',
      sortable: false,
      resizable: false,
      minWidth: 60,
      maxWidth: 70,
      cell: (item) => (
        item.image_url ? (
          <img src={item.image_url} alt={item.name} className="rounded"
            style={{ width: '40px', height: '40px', objectFit: 'cover' }} />
        ) : (
          <div className="d-flex align-items-center justify-content-center rounded bg-light"
            style={{ width: '40px', height: '40px' }}>
            <i className="bi bi-box-seam text-muted"></i>
          </div>
        )
      ),
    },
    {
      id: 'sku',
      header: 'SKU',
      accessor: (i) => i.sku || '',
      minWidth: 100,
      cell: (item) => <code className="text-primary">{item.sku || '-'}</code>,
    },
    {
      id: 'name',
      header: 'Nombre',
      accessor: 'name',
      minWidth: 200,
      cell: (item) => (
        <div>
          <strong>{item.name}</strong>
          {item.description && (
            <small className="d-block text-muted">{item.description}</small>
          )}
        </div>
      ),
    },
    {
      id: 'category',
      header: 'Categoría',
      accessor: (i) => INVENTORY_CATEGORY_LABELS[i.category] || 'Sin categoría',
      minWidth: 120,
      cell: (item) => getCategoryBadge(item),
    },
    {
      id: 'unit',
      header: 'Unidad',
      accessor: 'unit',
      minWidth: 80,
    },
    {
      // Decisión 8 (Jorge): dónde vive el material. Paridad de carriles — el
      // agente ya contesta "Bodega Principal" por MCP (stock_locations, #380);
      // sin esta columna el web app, que es el driver diario de Jorge, era el
      // único lugar donde la ubicación no existía.
      id: 'location',
      header: 'Bodega',
      accessor: (i) => i.default_location?.label || '',
      minWidth: 130,
      cell: (item) => item.default_location?.label ? (
        <span className="text-nowrap">
          <i className="bi bi-geo-alt me-1 text-muted"></i>
          {item.default_location.label}
        </span>
      ) : (
        <span className="text-muted">Sin bodega</span>
      ),
    },
    {
      id: 'stock',
      header: 'Stock',
      accessor: (i) => i.current_stock || 0,
      align: 'right',
      minWidth: 90,
      cell: (item) => (
        <>
          <strong className={isLowStock(item.current_stock, item.min_stock_level) ? 'text-danger' : ''}>
            {item.current_stock}
          </strong>
          <small className="text-muted d-block">min: {item.min_stock_level}</small>
        </>
      ),
    },
    {
      id: 'batches',
      header: 'Lotes',
      accessor: (i) => batchCounts[i.id] || 0,
      align: 'center',
      minWidth: 90,
      cell: (item) => batchCounts[item.id] > 0 ? (
        <Badge bg="info" style={{ cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); navigate(`/inventario/${item.id}/lotes`); }}
          title="Ver lotes">
          {batchCounts[item.id]} lote{batchCounts[item.id] > 1 ? 's' : ''}
        </Badge>
      ) : <Badge bg="secondary">Sin lotes</Badge>,
    },
    {
      id: 'sale_price',
      header: 'Precio Lista',
      accessor: (i) => i.sale_price || 0,
      align: 'right',
      minWidth: 110,
      cell: (item) => item.sale_price ? formatCurrency(item.sale_price) : <span className="text-muted">-</span>,
    },
    // admin-only: purchase cost data — not shown to clients or technicians
    ...(isAdmin ? [{
      id: 'avg_cost',
      header: 'Costo Prom.',
      accessor: (i) => i.weighted_avg_cost || i.unit_cost || 0,
      align: 'right',
      minWidth: 110,
      cell: (item) => formatCurrency(item.weighted_avg_cost || item.unit_cost),
    }] : []),
    {
      id: 'status',
      header: 'Estado',
      accessor: 'current_stock',
      minWidth: 110,
      cell: (item) => getStockBadge(item),
    },
    {
      id: 'actions',
      header: '',
      sortable: false,
      resizable: false,
      align: 'center',
      noSelect: true,
      minWidth: 50,
      maxWidth: 60,
      cell: (item) => (
        <div data-no-select>
          <KebabMenu label={`Acciones de ${item.name}`}>
              {canEdit && (
                <>
                  <Dropdown.Item onClick={() => handleAdjustStock(item)}>
                    <i className="bi bi-arrow-left-right me-2"></i>Ajustar Stock
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => navigate(`/inventario/${item.id}/editar`)}>
                    <i className="bi bi-pencil me-2"></i>Editar
                  </Dropdown.Item>
                  {canEdit && item.item_type === 'tool' && (
                    <Dropdown.Item onClick={() => navigate(`/inventario/${item.id}/assets`)}>
                      <i className="bi bi-cpu me-2"></i>Gestionar Assets
                    </Dropdown.Item>
                  )}
                  <Dropdown.Divider />
                </>
              )}
              {batchCounts[item.id] > 0 && (
                <Dropdown.Item onClick={() => navigate(`/inventario/${item.id}/lotes`)}>
                  <i className="bi bi-layers me-2"></i>Ver Lotes
                </Dropdown.Item>
              )}
              {item.item_type === 'tool' && (
                <Dropdown.Item onClick={() => navigate(`/inventario/${item.id}/assets`)}>
                  <i className="bi bi-upc-scan me-2"></i>Gestionar assets
                </Dropdown.Item>
              )}
              <Dropdown.Item onClick={() => navigate(`/inventario/${item.id}/movimientos`)}>
                <i className="bi bi-clock-history me-2"></i>Ver Movimientos
              </Dropdown.Item>
          </KebabMenu>
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [navigate, canEdit, batchCounts, isAdmin]);

  // Get stock status badge
  const getStockBadge = (item) => {
    if (item.current_stock === 0) {
      return <Badge bg="danger">Agotado</Badge>;
    } else if (isLowStock(item.current_stock, item.min_stock_level)) {
      return <Badge bg="warning" text="dark">Bajo Stock</Badge>;
    } else {
      return <Badge bg="success">OK</Badge>;
    }
  };

  // Get category badge (graceful fallback for missing/unmapped categories)
  const getCategoryBadge = (item) => {
    const label = INVENTORY_CATEGORY_LABELS[item.category];
    return (
      <Badge bg="secondary" className="fw-normal">
        {label || 'Sin categoría'}
      </Badge>
    );
  };

  const handleAdjustStock = (item) => {
    setSelectedItem(item);
    setShowMovementModal(true);
  };

  const handleMovementSuccess = () => {
    setShowMovementModal(false);
    setSelectedItem(null);
    refetch();
  };

  if (isLoading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Cargando inventario...</span>
        </Spinner>
      </Container>
    );
  }

  // "Falló la consulta" NO es "no hay materiales en el inventario". Sin este
  // bloque, una caída de red o un rechazo de RLS pintaba el estado vacío de más
  // abajo y el inventario parecía borrado.
  if (isError) {
    return (
      <DashboardLayout>
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>Error al cargar el inventario</Alert.Heading>
            <p>{error?.message || 'Ocurrió un error inesperado'}</p>
            <Button variant="outline-danger" onClick={() => refetch()}>
              Reintentar
            </Button>
          </Alert>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Container fluid className="py-4 inventory-list">
        {/* Header */}
        <Row className="mb-4">
        <Col>
          <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
            <div>
              <h1 className="h3 mb-0">Inventario</h1>
              {/* El total del conjunto filtrado, no el largo de la página: antes
                  decía "20 artículos" habiendo 54. */}
              <p className="text-muted mb-0">{totalCount} artículos</p>
            </div>
            {canEdit && (
              <Button
                variant="primary"
                onClick={() => navigate(
                  activeTab === 'herramientas' ? '/inventario/nuevo?tipo=tool' : '/inventario/nuevo'
                )}
              >
                <i className="bi bi-plus-circle me-2"></i>
                <span className="d-none d-sm-inline">
                  {activeTab === 'herramientas' ? 'Agregar Herramienta' : 'Agregar Material'}
                </span>
                <span className="d-inline d-sm-none">Agregar</span>
              </Button>
            )}
          </div>
        </Col>
      </Row>

      {/* Tab Navigation */}
      <Row className="mb-3">
        <Col>
          <Nav variant="tabs">
            <Nav.Item>
              <Nav.Link active={activeTab === 'materiales'} onClick={() => setActiveTab('materiales')}>
                <i className="bi bi-box-seam me-2"></i>Materiales
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link active={activeTab === 'herramientas'} onClick={() => setActiveTab('herramientas')}>
                <i className="bi bi-tools me-2"></i>Herramientas
              </Nav.Link>
            </Nav.Item>
          </Nav>
        </Col>
      </Row>

      {/* Summary Statistics */}
      {summary.totalItems > 0 && (
        <Row className="mb-4 g-3">
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-primary bg-opacity-10 rounded p-3">
                      <i className="bi bi-box-seam fs-4 text-primary"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Total Artículos</p>
                    <h3 className="mb-0">{formatNumber(summary.totalItems || 0)}</h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-success bg-opacity-10 rounded p-3">
                      <i className="bi bi-currency-dollar fs-4 text-success"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Valor Total</p>
                    <h3 className="mb-0 fs-5">{formatCurrency(summary.totalValue || 0)}</h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-warning bg-opacity-10 rounded p-3">
                      <i className="bi bi-exclamation-triangle fs-4 text-warning"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Bajo Stock</p>
                    <h3 className="mb-0">{formatNumber(summary.lowStockItems || 0)}</h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12} sm={6} lg={3}>
            <Card className="h-100 border-0 shadow-sm">
              <Card.Body>
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-danger bg-opacity-10 rounded p-3">
                      <i className="bi bi-x-circle fs-4 text-danger"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <p className="text-muted mb-0 small">Agotados</p>
                    <h3 className="mb-0">{formatNumber(summary.outOfStockItems || 0)}</h3>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Low Stock Alert */}
      {!showLowStockOnly && summary.lowStockItems > 0 && (
        <Row className="mb-3">
          <Col>
            <Alert variant="warning" className="d-flex align-items-center mb-0">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              <div className="flex-grow-1">
                Tienes {summary.lowStockItems} artículo{summary.lowStockItems > 1 ? 's' : ''} con bajo stock
                {summary.outOfStockItems > 0 && ` y ${summary.outOfStockItems} agotado${summary.outOfStockItems > 1 ? 's' : ''}`}.
              </div>
              <Button
                variant="warning"
                size="sm"
                onClick={() => setShowLowStockOnly(true)}
                className="ms-2"
              >
                Ver Artículos
              </Button>
            </Alert>
          </Col>
        </Row>
      )}

      {/* Search & Filters */}
      <div className="inventory-search-filter mb-4">
        {/* Global search bar + filter toggle */}
        <div className="inventory-global-search">
          <InputGroup className="flex-grow-1">
            <InputGroup.Text>
              {isFetching && !isLoading ? (
                <Spinner animation="border" size="sm" className="text-muted" />
              ) : (
                <i className="bi bi-search"></i>
              )}
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Buscar inventario..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              aria-label="Buscar en inventario"
            />
          </InputGroup>
          <ViewToggle viewMode={viewMode} onToggle={setViewMode} />
          <Button
            variant={activeFiltersCount > 0 ? 'primary' : 'outline-secondary'}
            onClick={toggleFiltersPanel}
            aria-expanded={isFiltersExpanded}
            aria-controls="inventory-advanced-filters"
            className="filter-toggle-btn"
          >
            <i className={`bi bi-chevron-${isFiltersExpanded ? 'up' : 'down'}`}></i>
            <span className="d-none d-md-inline ms-1">Filtros</span>
            {activeFiltersCount > 0 && (
              <Badge bg="light" text="dark" pill className="filter-count-badge">
                {activeFiltersCount}
              </Badge>
            )}
          </Button>
          <Button
            variant="outline-info"
            onClick={() => navigate('/inventario/valuacion')}
            className="d-none d-md-flex align-items-center"
          >
            <i className="bi bi-graph-up me-2"></i>
            Valuación
          </Button>
        </div>

        {/* Collapsible advanced filters panel */}
        <Collapse in={isFiltersExpanded}>
          <div id="inventory-advanced-filters">
            <div className="advanced-filters-panel">
              <Row className="g-2 g-md-3">
                <Col xs={12} sm={6} lg={3}>
                  <Form.Label className="small text-muted mb-1">Categoria</Form.Label>
                  <Form.Select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value)}
                    aria-label="Filtrar por categoria"
                    size="sm"
                  >
                    <option value="">Todas las categorías</option>
                    {Object.entries(INVENTORY_CATEGORY_LABELS).map(([key, label]) => (
                      <option key={key} value={key}>{label}</option>
                    ))}
                  </Form.Select>
                </Col>

                <Col xs={12} sm={6} lg={3}>
                  <Form.Label className="small text-muted mb-1">Estado de Stock</Form.Label>
                  <Form.Check
                    type="switch"
                    id="low-stock-filter"
                    label="Solo bajo stock"
                    checked={showLowStockOnly}
                    onChange={(e) => setShowLowStockOnly(e.target.checked)}
                    className="mt-1"
                  />
                </Col>

                <Col xs={12} sm={6} lg={3}>
                  <Form.Label className="small text-muted mb-1">Ordenar por</Form.Label>
                  <Form.Select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    aria-label="Ordenar por"
                    size="sm"
                  >
                    <option value="name">Nombre (A-Z)</option>
                    <option value="sku">SKU</option>
                    <option value="stock-asc">Stock (menor a mayor)</option>
                    <option value="stock-desc">Stock (mayor a menor)</option>
                  </Form.Select>
                </Col>

                <Col xs={12} sm={6} lg={3} className="d-flex align-items-end">
                  <Button
                    variant="outline-info"
                    className="w-100 d-md-none"
                    onClick={() => navigate('/inventario/valuacion')}
                    size="sm"
                  >
                    <i className="bi bi-graph-up me-2"></i>
                    Ver Valuacion
                  </Button>
                </Col>
              </Row>

              {/* Clear filters button */}
              {hasActiveFilters && (
                <div className="d-flex justify-content-end mt-3">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleClearFilters}
                    className="clear-filters-btn"
                  >
                    <i className="bi bi-x-circle me-1"></i>
                    Limpiar filtros
                    {activeFiltersCount > 0 && ` (${activeFiltersCount})`}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </Collapse>
      </div>

      {/* Inventory Content */}
      {tabFilteredItems.length > 0 ? (
        <>
        {isTableView ? (
        <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Body className="p-0">
              <DataTable
                tableId="inventory"
                data={tabFilteredItems}
                rowKey={(i) => i.id}
                columns={inventoryColumns}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
        ) : (
        <Row className="g-3">
          {tabFilteredItems.map((item) => (
            <Col xs={12} sm={6} lg={4} key={item.id}>
              <Card className="shadow-sm">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    {/* Product Image */}
                    <div className="me-3">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="rounded"
                          style={{
                            width: '60px',
                            height: '60px',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <div
                          className="d-flex align-items-center justify-content-center rounded bg-light"
                          style={{ width: '60px', height: '60px' }}
                        >
                          <i className="bi bi-box-seam text-muted fs-4"></i>
                        </div>
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <h6 className="mb-1">{item.name}</h6>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {item.sku && (
                          <code className="text-primary small">{item.sku}</code>
                        )}
                        {getCategoryBadge(item)}
                      </div>
                      {item.description && (
                        <p className="text-muted small mb-0 mt-1">{item.description}</p>
                      )}
                    </div>
                    <div className="ms-2">
                      {getStockBadge(item)}
                    </div>
                  </div>

                  <Row className="g-2 mb-3">
                    <Col xs={6}>
                      <small className="text-muted d-block">Stock Actual</small>
                      <strong className={isLowStock(item.current_stock, item.min_stock_level) ? 'text-danger' : ''}>
                        {item.current_stock} {item.unit}
                      </strong>
                      <small className="text-muted d-block">min: {item.min_stock_level}</small>
                    </Col>
                    <Col xs={6}>
                      <small className="text-muted d-block">Lotes</small>
                      {batchCounts[item.id] > 0 ? (
                        <Badge
                          bg="info"
                          style={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/inventario/${item.id}/lotes`)}
                        >
                          {batchCounts[item.id]} lote{batchCounts[item.id] > 1 ? 's' : ''}
                        </Badge>
                      ) : (
                        <Badge bg="secondary">Sin lotes</Badge>
                      )}
                    </Col>
                    <Col xs={6}>
                      <small className="text-muted d-block">Precio Lista</small>
                      <strong>{item.sale_price ? formatCurrency(item.sale_price) : '-'}</strong>
                    </Col>
                    {/* admin-only: purchase cost */}
                    {isAdmin && (
                      <Col xs={6}>
                        <small className="text-muted d-block">Costo Prom.</small>
                        <strong>{formatCurrency(item.weighted_avg_cost || item.unit_cost)}</strong>
                      </Col>
                    )}
                  </Row>

                  <div className="d-flex justify-content-end">
                    <KebabMenu label={`Acciones de ${item.name}`}>
                        {canEdit && (
                          <>
                            <Dropdown.Item onClick={() => handleAdjustStock(item)}>
                              <i className="bi bi-arrow-left-right me-2"></i>
                              Ajustar Stock
                            </Dropdown.Item>
                            <Dropdown.Item onClick={() => navigate(`/inventario/${item.id}/editar`)}>
                              <i className="bi bi-pencil me-2"></i>
                              Editar
                            </Dropdown.Item>
                            {canEdit && item.item_type === 'tool' && (
                              <Dropdown.Item onClick={() => navigate(`/inventario/${item.id}/assets`)}>
                                <i className="bi bi-cpu me-2"></i>
                                Gestionar Assets
                              </Dropdown.Item>
                            )}
                            <Dropdown.Divider />
                          </>
                        )}
                        {batchCounts[item.id] > 0 && (
                          <Dropdown.Item onClick={() => navigate(`/inventario/${item.id}/lotes`)}>
                            <i className="bi bi-layers me-2"></i>
                            Ver Lotes
                          </Dropdown.Item>
                        )}
                        <Dropdown.Item onClick={() => navigate(`/inventario/${item.id}/movimientos`)}>
                          <i className="bi bi-clock-history me-2"></i>
                          Ver Movimientos
                        </Dropdown.Item>
                    </KebabMenu>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        )}
        <PaginationBar
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(size) => { setPageSize(size); setPage(0); }}
          itemLabel={activeTab === 'herramientas' ? 'herramientas' : 'materiales'}
        />
        </>
      ) : (
        <div className="p-5 text-center text-muted">
          <i className={`bi ${activeTab === 'herramientas' ? 'bi-tools' : 'bi-box-seam'} fs-1 d-block mb-3`}></i>
          <p className="mb-0">
            {searchTerm || showLowStockOnly
              ? 'No se encontraron artículos con los filtros aplicados'
              : activeTab === 'herramientas'
                ? 'No hay herramientas registradas'
                : 'No hay materiales en el inventario'}
          </p>
        </div>
      )}

      {/* Stock Movement Modal */}
      {showMovementModal && selectedItem && (
        <StockMovementModal
          show={showMovementModal}
          onHide={() => {
            setShowMovementModal(false);
            setSelectedItem(null);
          }}
          inventoryItem={selectedItem}
          onSuccess={handleMovementSuccess}
        />
      )}
      </Container>
    </DashboardLayout>
  );
};

export default InventoryList;
